import type { DomainId } from "./domains";
import { ALL_DOMAIN_IDS } from "./domains";

export type LogLevel =
  | "trace"
  | "debug"
  | "info"
  | "warn"
  | "error"
  | "fatal"
  | "silent";

export type ActiveLogDomains = ReadonlySet<DomainId> | "*";

export type DomainLogLevels = {
  [K in DomainId]: LogLevel;
};

export const DEFAULT_LOG_LEVELS: DomainLogLevels = {
  GAME: "info",
  PHYSICS: "debug",
  NETWORKING: "info",
  PLAYER: "info",
  BOT: "debug",
  ASTEROIDS: "info",
  PELLETS: "info",
  MINES: "info",
  BULLETS: "info",
  COLLISION: "debug",
  LOBBY: "info",
  QUEUE: "info",
  DATABASE: "info",
  AUTH: "info",
  METRICS: "info",
  STATS: "info",
  ROUTES: "info",
  BUG: "warn",
  OG: "info",
  STARTUP: "info",
  RENDERING: "debug",
  EFFECTS: "info",
  THEME: "info",
  INPUT: "debug",
  UI: "info",
  AUDIO: "info",
  WEBSOCKET: "info",
  LOBBY_CLIENT: "info",
  CLIENT: "info",
  ECS: "info",
  REACT: "warn",
  MODALS: "info",
  TOAST: "info",
  PERF: "info",
};

const ACTIVE_DOMAINS_ARRAY: DomainId[] = ALL_DOMAIN_IDS;

export const ACTIVE_LOG_DOMAINS: ActiveLogDomains =
  (typeof process !== "undefined" && process.env?.NODE_ENV) === "production"
    ? new Set<DomainId>(ACTIVE_DOMAINS_ARRAY)
    : new Set<DomainId>(ACTIVE_DOMAINS_ARRAY);

export function isDomainActive(domain: DomainId): boolean {
  if (ACTIVE_LOG_DOMAINS === "*") return true;
  return (ACTIVE_LOG_DOMAINS as ReadonlySet<DomainId>).has(domain);
}

export function getDomainLogLevel(domain: DomainId): LogLevel {
  return DEFAULT_LOG_LEVELS[domain];
}

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
  silent: 70,
};

export function shouldLog(domain: DomainId, level: LogLevel): boolean {
  if (!isDomainActive(domain)) return false;

  const domainLevel = getDomainLogLevel(domain);
  const domainPriority = LOG_LEVEL_PRIORITY[domainLevel];
  const messagePriority = LOG_LEVEL_PRIORITY[level];

  return messagePriority >= domainPriority;
}

export function overrideLogLevels(
  overrides: Partial<DomainLogLevels>,
): DomainLogLevels {
  return { ...DEFAULT_LOG_LEVELS, ...overrides };
}

export function disableDomains(domains: DomainId[]): ActiveLogDomains {
  const active = new Set(ALL_DOMAIN_IDS);
  for (const domain of domains) {
    active.delete(domain);
  }
  return active;
}

export function enableOnlyDomains(domains: DomainId[]): ActiveLogDomains {
  return new Set(domains);
}

export function isModuleActive(module: string): boolean {
  return isDomainActive(module as DomainId);
}

export { ALL_DOMAIN_IDS as ALL_LOG_MODULES };
export const ACTIVE_LOG_MODULES = ACTIVE_LOG_DOMAINS;
export type LogModule = DomainId;
